import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button } from 'react-bootstrap';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BRAND } from './auditTones';

/**
 * The upload step.
 *
 * The file is handed straight up to the parent - it is NOT parsed here. The
 * audit compares against live prescription data, so the comparison happens
 * server-side; the browser only holds the file so it can be re-sent when the
 * reviewer changes the column mapping or toggles strict mode.
 *
 * Icons are the FontAwesome SVG component, not `fas fa-*` CSS classes: this app
 * never loads FontAwesome's stylesheet, it registers an icon library in
 * helpers/initFA.js, so a class-based icon renders as nothing at all.
 */
const CsvDropzone = ({ file, onFile, onClear, disabled, rowCount }) => {
  const onDrop = useCallback(
    accepted => {
      if (accepted && accepted[0]) onFile(accepted[0]);
    },
    [onFile]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'text/csv': ['.csv'], 'application/vnd.ms-excel': ['.csv'] },
    multiple: false,
    disabled
  });

  if (file) {
    return (
      <div className="border rounded-3 p-3 d-flex align-items-center bg-body-tertiary">
        <div
          className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 me-3"
          style={{ width: 46, height: 46, backgroundColor: 'rgba(0,210,122,0.12)', color: '#00864e' }}
        >
          <FontAwesomeIcon icon="file-import" className="fs-7" />
        </div>
        <div className="flex-grow-1 min-w-0">
          <div className="fw-semibold text-truncate" title={file.name}>
            {file.name}
          </div>
          <div className="fs-10 text-600">
            {rowCount !== null && rowCount !== undefined ? (
              <>
                <FontAwesomeIcon icon="check-circle" className="text-success me-1" />
                {rowCount} medication row{rowCount === 1 ? '' : 's'} read
              </>
            ) : (
              `${Math.max(1, Math.round(file.size / 1024))} KB`
            )}
          </div>
        </div>
        <Button
          variant="link"
          size="sm"
          className="text-danger text-decoration-none flex-shrink-0"
          onClick={onClear}
          disabled={disabled}
        >
          <FontAwesomeIcon icon="trash" className="me-1" />
          Remove
        </Button>
      </div>
    );
  }

  return (
    <div
      {...getRootProps({
        className: classNames(
          'rounded-3 p-4 text-center flex-grow-1 d-flex flex-column justify-content-center',
          { 'opacity-50': disabled }
        ),
        style: {
          cursor: disabled ? 'not-allowed' : 'pointer',
          minHeight: 176,
          border: `2px dashed ${isDragActive ? BRAND.base : '#c6d0e0'}`,
          backgroundColor: isDragActive ? BRAND.softer : 'transparent',
          transition: 'border-color .15s ease, background-color .15s ease'
        }
      })}
    >
      <input {...getInputProps()} />
      <div
        className="d-flex align-items-center justify-content-center rounded-circle mx-auto mb-3"
        style={{
          width: 56,
          height: 56,
          backgroundColor: isDragActive ? BRAND.soft : 'rgba(120,132,156,0.12)',
          color: isDragActive ? BRAND.base : '#5e6e82'
        }}
      >
        <FontAwesomeIcon icon="file-upload" className="fs-6" />
      </div>
      <p className="mb-1 fw-semibold fs-9">
        {isDragActive ? 'Drop the file to read it' : 'Drag a medication export here'}
      </p>
      <p className="mb-0 text-600 fs-10">
        or{' '}
        <span className="fw-medium" style={{ color: BRAND.base }}>
          browse for a file
        </span>{' '}
        · .csv only
      </p>
      <p className="mb-0 text-500 fs-11 mt-2">
        Columns are detected automatically — you can correct them before the audit runs
      </p>
    </div>
  );
};

CsvDropzone.propTypes = {
  file: PropTypes.object,
  onFile: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  rowCount: PropTypes.number
};

export default CsvDropzone;
